/**
 * The gallery's contacts, decided: who the firm has dealt with or added,
 * split into registry identities and names it typed, with the note, the
 * timeline and the CSV that go with them.
 *
 * Pure. One read's rows go in; the two sections, the tiles, the chips, the
 * add-contact form's verdict and the export text come out. The copy is
 * `contacts.constants.ts`; the reads, the three writes and the drawer and
 * modal state are `useContactsController.ts`. The estate's Contacts screen
 * remounts the same pair.
 *
 * Nothing here counts what the server already counted — works, edges and
 * spend arrive decided, because they are answers about rows this browser
 * may not be shown. Spend is one figure per currency and never a total,
 * on screen and in the CSV alike, and a CSV cell beginning with `=` is
 * guarded because the name came from free text.
 */

import type {
  AddContactRequest,
  Contact,
  ContactEvent,
  ContactSpend,
  ReaderStamp,
} from "@ondo/shared";
import { describeContactKind, formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatDay } from "../lib/dates.ts";
import { initialsOf } from "../lib/names.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { CONTACTS_COPY } from "./contacts.constants.ts";

/**
 * Which relationship the reader is filtering by, or all of them.
 *
 * A string rather than a union for the reason `Contact.kinds` is: the vocabulary
 * lives in SQL, and a kind added there must show up as an unfiltered row rather
 * than a build failure.
 */
export type ContactFilter = string;

/** One contact, as the row prints it — the mock's eight columns. */
export interface ContactRow {
  /** Stable across renders and unique across both sections. */
  key: string;
  /** Null for a named contact — and the only thing that says which kind it is. */
  ondoId: string | null;
  name: string;
  initials: string;
  /**
   * Under the name: the firm's own email where one was recorded, then the
   * ONDO id line or the sentence that says there is none.
   */
  sub: string;
  /** The firm's own recorded email, bare, for the CSV. '' when none. */
  email: string;
  /**
   * The Type column: the firm's own label where it set one, else the
   * relationship in the reader's words — 'Consignor · Artist owed'. The
   * registry's capacity is deliberately neither: what somebody IS cannot be
   * read about them, and the audit of this screen's mock said so.
   */
  type: string;
  /** The firm's own text, or '—'. */
  location: string;
  /** Distinct works on open edges, as a figure. '—' when none. */
  heldNow: string;
  /** Distinct works lifetime, as a figure. '—' when none. */
  historic: string;
  verified: boolean;
  live: boolean;
  /** '£1,850,000 · $310,000', or '—'. One figure per currency, never a total. */
  spend: string;
  /** 'Last 22 Aug 2026'. */
  last: string;
  /** The firm's own note, and the row holding it. Null when none is kept. */
  noteId: string | null;
  note: string | null;
  tags: string[];
  /** The added_contacts row, when the firm added this contact by hand. */
  addedId: string | null;
  /** What `setContactNote` needs to address this row: an id, or the exact name. */
  subject: { ondoId: string } | { subjectName: string };
}

export type ContactsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      tiles: Tile[];
      /** Every relationship present in the data, plus 'All' first. */
      filters: { key: ContactFilter; label: string }[];
      filter: ContactFilter;
      attached: ContactRow[];
      named: ContactRow[];
      /** Set when the whole list is empty, before any filter is applied. */
      empty: boolean;
      /** Set when a filter has emptied an otherwise populated list. */
      filteredOut: boolean;
    };

/** One line of a contact's shared history. */
export interface TimelineRow {
  key: string;
  when: string;
  what: string;
  /** The work, when there is one. An affiliation has none. */
  title: string | null;
  href: string | null;
  /**
   * The work's photograph, stamped by the SERVER with this reader's name — an
   * object URL over bytes the API composited, never a signed storage URL. Null
   * when the work has no photograph, when storage refused it, or when the row
   * is an affiliation and has no work at all.
   */
  imageUrl: string | null;
  live: boolean;
}

export type TimelineView =
  | { kind: "closed" }
  | { kind: "loading"; name: string }
  | { kind: "error"; name: string; message: string }
  | { kind: "ready"; name: string; rows: TimelineRow[]; empty: string | null };

/**
 * The contacts screen, assembled from one read.
 *
 * NOTHING HERE COUNTS ANYTHING THE SERVER ALREADY COUNTED. `works`, `edges` and
 * the per-currency spend arrive decided, because they are answers about rows
 * this browser cannot see — a tally recomputed here would be a second opinion
 * about money, formed from a list that policies may have narrowed.
 *
 * THE TWO SECTIONS ARE THE MODEL'S ONE REAL JOB. `ondoId` is the whole of the
 * distinction, and it is drawn here rather than in the view so that no component
 * can ever decide to render the two alike.
 */
export function toContactsView({
  contacts,
  filter,
  search = "",
}: {
  contacts: Contact[] | null;
  filter: ContactFilter;
  /** A filter over loaded rows — matched on name, email and ONDO id. */
  search?: string;
}): ContactsView {
  if (contacts === null) return { kind: "loading", message: CONTACTS_COPY.loading };

  const needle = search.trim().toLowerCase();
  const matching = contacts.filter(
    (contact) =>
      (filter === ALL || contact.kinds.includes(filter)) &&
      (needle === "" ||
        contact.displayName.toLowerCase().includes(needle) ||
        (contact.email ?? "").toLowerCase().includes(needle) ||
        (contact.ondoId ?? "").toLowerCase().includes(needle)),
  );

  const attached = matching.filter((contact) => contact.ondoId !== null).map(toRow);
  const named = matching.filter((contact) => contact.ondoId === null).map(toRow);

  return {
    kind: "ready",
    tiles: tilesFor(contacts),
    filters: filtersFor(contacts),
    filter,
    attached,
    named,
    empty: contacts.length === 0,
    filteredOut: contacts.length > 0 && matching.length === 0,
  };
}

/** The sentinel for "no filter". Not a relationship, so it cannot collide with one. */
export const ALL = "*";

function toRow(contact: Contact): ContactRow {
  const meta = contact.ondoId
    ? `${contact.ondoId} · ${contact.isOrg ? "organisation" : "person"}`
    : CONTACTS_COPY.namedMeta;

  return {
    // The ONDO id when there is one; otherwise the name, which is the only
    // thing that identifies a named contact at all. Prefixed so a contact
    // called 'ONDO-1031' could never collide with the principal of that id.
    key: contact.ondoId ? `id:${contact.ondoId}` : `name:${contact.displayName}`,
    ondoId: contact.ondoId,
    name: contact.displayName,
    initials: initialsOf(contact.displayName),
    sub: contact.email ? `${contact.email} · ${meta}` : meta,
    email: contact.email ?? "",
    type: contact.kindText ?? contact.kinds.map(describeKind).join(" · "),
    location: contact.location ?? CONTACTS_COPY.spendNone,
    heldNow: figure(contact.worksNow),
    historic: figure(contact.works),
    verified: contact.verified,
    live: contact.live,
    spend: describeSpend(contact.spend),
    last: formatDay(contact.lastAt),
    noteId: contact.noteId,
    note: contact.note,
    tags: contact.tags,
    addedId: contact.addedId,
    // The two kinds of subject, decided once here so no component has to.
    subject: contact.ondoId
      ? { ondoId: contact.ondoId }
      : { subjectName: contact.displayName },
  };
}

/** '7', or '—' — a zero on a CRM column reads as data, and an absence is not. */
function figure(count: number): string {
  return count > 0 ? String(count) : CONTACTS_COPY.spendNone;
}

/**
 * 'Consignor', 'Sold for them' — the relationship in the reader's words.
 *
 * DELEGATES TO `@ondo/shared` SINCE OND-67, because the server writes these
 * same words into the exported file's "Type" column. Two copies of the map
 * would mean the file and the screen could describe the same row differently
 * the day a kind was added to one of them.
 */
export function describeKind(kind: string): string {
  return describeContactKind(kind);
}

/**
 * '£1,850,000 · $310,000'.
 *
 * ONE FIGURE PER CURRENCY, joined rather than added. The schema refuses to add
 * two currencies until a director states a rate, and this is the screen where
 * somebody would most casually break that.
 */
export function describeSpend(spend: ContactSpend[] | null): string {
  if (!spend || spend.length === 0) return CONTACTS_COPY.spendNone;

  return spend.map((money) => formatMoney(money)).join(" · ");
}

/** The add-contact modal's draft — one form, two branches, picked by `mode`. */
export interface AddContactDraft {
  mode: "onOndo" | "offOndo";
  ondoId: string;
  name: string;
  email: string;
  location: string;
  kind: string;
}

export const EMPTY_ADD_DRAFT: AddContactDraft = {
  mode: "onOndo",
  ondoId: "",
  name: "",
  email: "",
  location: "",
  kind: "",
};

/** The modal, already decided. */
export interface AddContactView {
  open: boolean;
  draft: AddContactDraft;
  /** Shape only. The server decides everything about existence. */
  malformed: boolean;
  /** The server's own sentence on a refusal, verbatim. */
  problem: string | null;
  busy: boolean;
  canSubmit: boolean;
  kindOptions: readonly string[];
}

/**
 * Shape, and nothing else — the third copy of this one-line test, beside
 * work.model.ts and consign.model.ts, which already carry one each. The server
 * decides everything about existence.
 */
export function isOndoIdShape(value: string): boolean {
  return /^ONDO-[0-9]{4,}$/i.test(value.trim());
}

/**
 * NO ELIGIBILITY CHECK, and the absence is the design. `POST /v1/eligibility`
 * requires a subject the caller already holds, and adding a contact has none —
 * RequestRepresentation's argument: where the caller holds nothing, an
 * incremental check would be a code oracle. The submit is answered by
 * `ondo.add_contact()` with one sentence per branch, shown verbatim.
 */
export function toAddContactView(input: {
  open: boolean;
  draft: AddContactDraft;
  busy: boolean;
  problem: string | null;
}): AddContactView {
  const typedId = input.draft.ondoId.trim();
  const malformed =
    input.draft.mode === "onOndo" && typedId.length > 0 && !isOndoIdShape(typedId);

  return {
    open: input.open,
    draft: input.draft,
    malformed,
    problem: input.problem,
    busy: input.busy,
    canSubmit:
      !input.busy &&
      (input.draft.mode === "onOndo"
        ? isOndoIdShape(typedId)
        : input.draft.name.trim().length > 0),
    kindOptions: CONTACTS_COPY.addKindOptions,
  };
}

/**
 * The wire body, with the branch rule enforced by construction: an ONDO id
 * alone, or a name with whatever else was typed. Blanks are dropped rather
 * than sent as empty strings, so the server never has to decide what '' means.
 */
export function buildAddContactRequest(draft: AddContactDraft): AddContactRequest {
  if (draft.mode === "onOndo") return { ondoId: draft.ondoId.trim() };

  const email = draft.email.trim();
  const location = draft.location.trim();
  const kind = draft.kind.trim();

  return {
    name: draft.name.trim(),
    ...(email ? { email } : {}),
    ...(location ? { location } : {}),
    ...(kind ? { kind } : {}),
  };
}

/**
 * The chips: 'All', then every relationship actually present.
 *
 * Derived from the data rather than from the vocabulary, so a gallery is never
 * offered a filter that would empty its own screen — the argument Inventory's
 * artist chips already make.
 */
function filtersFor(contacts: Contact[]): { key: ContactFilter; label: string }[] {
  const present = new Set<string>();
  for (const contact of contacts) for (const kind of contact.kinds) present.add(kind);

  return [
    { key: ALL, label: CONTACTS_COPY.all },
    ...[...present]
      .sort()
      .map((kind) => ({ key: kind, label: describeKind(kind) })),
  ];
}

/** Three counts, each arithmetic over the list below. Never a trend. */
function tilesFor(contacts: Contact[]): Tile[] {
  const attached = contacts.filter((contact) => contact.ondoId !== null).length;
  const live = contacts.filter((contact) => contact.live).length;

  return [
    {
      key: "attached",
      label: CONTACTS_COPY.tileContacts,
      value: String(attached),
      note: null,
    },
    {
      key: "named",
      label: CONTACTS_COPY.tileNamed,
      value: String(contacts.length - attached),
      note: null,
    },
    { key: "live", label: CONTACTS_COPY.tileLive, value: String(live), note: null },
  ];
}

/**
 * One contact's shared history, newest first.
 *
 * `thumbs` is keyed by WORK id and holds the STAMPED photographs — bytes the
 * server composited with this reader's name, fetched through the API rather
 * than signed in the browser. A work absent from the map simply has no picture
 * on its row, which is the same judgement `signWorkThumbnails` gets everywhere
 * else: being refused the bytes is a state, not a fault.
 */
export function toTimelineRows(
  events: ContactEvent[],
  workBase: string,
  thumbs: ReadonlyMap<string, string> = new Map(),
): TimelineRow[] {
  return events.map((event, index) => ({
    key: `${event.recordId}:${event.kind}:${index}`,
    when: formatDay(event.happenedAt),
    what: describeKind(event.kind),
    title: event.title,
    href: event.workId ? `${workBase}/${event.workId}` : null,
    imageUrl: (event.workId && thumbs.get(event.workId)) || null,
    live: event.live,
  }));
}

/**
 * THE CSV USED TO BE BUILT HERE, AND IS NOT ANY MORE — OND-67.
 *
 * `toCsv`, `csvFilename` and the cell guard were this module's, and the
 * controller turned their output into a `Blob` and clicked an anchor at it. The
 * file was assembled from rows already on the page, which was deliberate and is
 * exactly what had to change: a file the browser builds is a file no server
 * ever hears about, so nothing could refuse one, bound one, or write down that
 * it happened — and the file named nobody.
 *
 * It now lives in `apps/api/src/contacts/collector-list-export.ts`, beside the
 * audit row it cannot be released without. Every decision it carried came with
 * it: what is on screen and nothing more, one figure per currency and never a
 * total, the section label on each row, and the apostrophe in front of a name
 * that would otherwise be a spreadsheet formula.
 *
 * WHAT THIS MODULE KEEPS is the stamp — who the server says is reading — and
 * the scope the export button sends, because both are things the screen shows.
 */

/** The reader stamp as the screen prints it, in two lines. */
export interface StampView {
  /** 'Dr Petra Kohl · ONDO-1042', and the firm when there is one. */
  who: string;
  /** 'Read 05 Oct 2026 at 09:20 UTC'. */
  when: string;
  /** The sentence that says what a stamp is and is not. */
  note: string;
}

/**
 * The stamp, decided.
 *
 * SERVER-SUPPLIED, EVERY FIELD. This takes the `ReaderStamp` that came back on
 * the same read as the rows and formats it; it does not assemble an identity
 * out of anything the browser knows. If it read the name from `useMe` instead,
 * the screen and the exported file would have two sources for the same claim
 * and nothing would keep them in step.
 *
 * THE INSTANT IS PRINTED IN UTC, matching the stamp inside the exported file.
 * A reader comparing the screen with the file they just took should see the
 * same minute.
 */
export function toStampView(stamp: ReaderStamp): StampView {
  const at = new Date(stamp.at);
  const when = Number.isNaN(at.getTime())
    ? stamp.at
    : `${formatDay(stamp.at, { utc: true })} at ${at.toISOString().slice(11, 16)} UTC`;

  return {
    who: stamp.actingName
      ? `${stamp.name} · ${stamp.ondoId} · acting for ${stamp.actingName} (${stamp.actingOndoId})`
      : `${stamp.name} · ${stamp.ondoId}`,
    when: `${CONTACTS_COPY.stampRead} ${when}`,
    note: CONTACTS_COPY.stampNote,
  };
}

/**
 * What the export button sends, taken off the screen's own two controls.
 *
 * `ALL` becomes null rather than being sent: the sentinel is this module's and
 * means "no filter", and the audit row records an absent filter as an export of
 * the whole list rather than as an export filtered by `*`.
 */
export function toExportScope(input: { filter: ContactFilter; search: string }): {
  filter: string | null;
  search: string | null;
} {
  return {
    filter: input.filter === ALL ? null : input.filter,
    search: input.search.trim() === "" ? null : input.search.trim(),
  };
}

export function describeContactsError(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return CONTACTS_COPY.forbidden;
  }
  return CONTACTS_COPY.failed;
}
